import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { db } from "../../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  limit,
} from "firebase/firestore";

import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import FadeInOut from "../../../components/animations/FadeInOut";
import Popup from "../../../components/Popup";
import ContainerViewComponent from "../../../components/ContainerViewComponent/ContainerViewComponent";

import {
  Card,
  Divider,
  CardHeader,
  LinearProgress,
  Box,
  IconButton,
  Button,
  Switch,
  Tooltip,
  Grid2,
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { customLocaleText } from "../../../utils/objects/CustomLocaleTextTables";

import useGetDataRiderByCompany from "../../../hooks/getDataRiderByCompany";
import useObtenerDatosdeCliente from "../../../hooks/obtenerDatosdeCliente";

// ---------------------------------------------
// Helpers
// ---------------------------------------------
const normalizeEmail = (v) => (v || "").trim().toLowerCase();

const validateEmail = (email) =>
  /^([a-zA-Z0-9_\.\-])+@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,})+$/.test(email);

// ---------------------------------------------
// Component
// ---------------------------------------------
const Drivers = () => {
  const navigate = useNavigate();

  // IDs a filtrar (dev)
  const developIDs = useMemo(() => ["LVY9zIL1OAbep6OvrrjEovLsKRD2"], []);

  const userUid = useSelector((store) => store.usuario);
  const uid = userUid?.user?.uid || null;

  const [companyId, setCompanyId] = useState("");
  const [emailRider, setEmailRider] = useState("");
  const [isValid, setIsValid] = useState(null); // null | boolean
  const [error, setError] = useState(null);

  const [showEdit, setShowEdit] = useState(false);

  const [rows, setRows] = useState([]); // ✅ siempre array
  const [selectedIDs, setSelectedIDs] = useState([]);

  const [inviting, setInviting] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null); // driverId/email para bloquear botón

  const [showPopup, setShowPopup] = useState(false);
  const [typePop, setTypePop] = useState(null);
  const [objRider, setObjRider] = useState({
    title: "showPopupWiewRiders",
    texto: "Lorem ipsum dolor.",
  });

  const { userdata, data } = useObtenerDatosdeCliente(uid);

  const { ridersDataByCompany } = useGetDataRiderByCompany(companyId);

  useEffect(() => {
    if (userdata?.userCompanyID) setCompanyId(userdata.userCompanyID);
  }, [data, userdata]);

  const togglePopup = () => setShowPopup((p) => !p);

  const toggleShowEditNew = () => {
    setError(null);
    setEmailRider("");
    setIsValid(null);
    setShowEdit((p) => !p);
  };

  const viewDriverPerfil = (id) => {
    if (!id) return;
    navigate(`/dashboard/drivers/${id}`);
  };

  // ---------------------------------------------
  // Firebase helpers (Company Drivers subcollection)
  // companies/{companyId}/companydrivers/{docId}
  // ---------------------------------------------
  const getCompanyDriversCollection = useCallback(() => {
    const companiesCol = collection(db, process.env.REACT_APP_COL_COMPANIES);
    const companyDocRef = doc(companiesCol, companyId);
    return collection(companyDocRef, "companydrivers");
  }, [companyId]);

  const findCompanyDriverDocRef = useCallback(
    async ({ driverId, email }) => {
      const companyDriversCol = getCompanyDriversCollection();

      const emailRaw = (email || "").trim();
      const emailLower = normalizeEmail(emailRaw);

      if (emailLower) {
        const refLower = doc(companyDriversCol, emailLower);
        const snapLower = await getDoc(refLower);
        if (snapLower.exists()) return refLower;
      }

      if (emailRaw) {
        const refRaw = doc(companyDriversCol, emailRaw);
        const snapRaw = await getDoc(refRaw);
        if (snapRaw.exists()) return refRaw;
      }

      // 2) Buscar por campo emailLower (si ya lo estás guardando)
      if (emailLower) {
        const qLowerField = query(
          companyDriversCol,
          where("emailLower", "==", emailLower),
          limit(1)
        );
        const sLowerField = await getDocs(qLowerField);
        if (!sLowerField.empty) return sLowerField.docs[0].ref;
      }

      // 3) Buscar por campo email (exact match; puede estar con mayúsculas)
      if (emailRaw) {
        const qEmailRaw = query(
          companyDriversCol,
          where("email", "==", emailRaw),
          limit(1)
        );
        const sEmailRaw = await getDocs(qEmailRaw);
        if (!sEmailRaw.empty) return sEmailRaw.docs[0].ref;
      }

      if (emailLower) {
        const qEmailLower = query(
          companyDriversCol,
          where("email", "==", emailLower),
          limit(1)
        );
        const sEmailLower = await getDocs(qEmailLower);
        if (!sEmailLower.empty) return sEmailLower.docs[0].ref;
      }

      // 4) Buscar por driverId
      if (driverId) {
        const qById = query(
          companyDriversCol,
          where("id", "==", driverId),
          limit(1)
        );
        const sById = await getDocs(qById);
        if (!sById.empty) return sById.docs[0].ref;
      }

      // 5) Último recurso: escanear pocos docs de la subcolección y comparar case-insensitive
      // (companydrivers suele ser pequeño por empresa)
      const all = await getDocs(companyDriversCol);
      for (const d of all.docs) {
        const dEmail = normalizeEmail(d.data()?.email);
        const dId = normalizeEmail(d.id);
        if (emailLower && (dEmail === emailLower || dId === emailLower)) {
          return d.ref;
        }
      }

      return null;
    },
    [getCompanyDriversCollection]
  );

  // ---------------------------------------------
  // Invite Rider (fix: preventDefault, paths, guardas)
  // ---------------------------------------------
  const invitarUsuariosRider = useCallback(
    async (e) => {
      e?.preventDefault();

      if (!companyId) {
        setError("Debe registrar su empresa para agregar usuarios");
        return;
      }

      const email = normalizeEmail(emailRider);

      if (!email) {
        setError("Ingrese un email válido");
        setIsValid(false);
        return;
      }

      if (!validateEmail(email)) {
        setError("Ingrese un email válido");
        setIsValid(false);
        return;
      }

      setError(null);
      setInviting(true);

      try {
        const companyDriversCol = getCompanyDriversCollection();
        const emailLower = normalizeEmail(emailRider);
        const existingRef = await findCompanyDriverDocRef({
          driverId: null,
          email: emailRider,
        });

        if (existingRef) {
          setError(
            "Ya existe una invitación o vínculo para este email (aunque esté con mayúsculas)."
          );
          setShowEdit(true);
          return;
        }

        // ✅ docId SIEMPRE lower
        const riderDocRef = doc(companyDriversCol, emailLower);

        // 2) Datos base
        const now = Date.now();
        const baseData = {
          invitedAt: now,
          email: emailLower, // ✅ guardado normalizado
          emailLower: emailLower, // ✅ campo extra recomendado
          createdAt: now,
          isblocked: false,
          isDeleted: false,
          isInvited: true,
          wasAccepted: false,
          id: "",
          bicciNumber: "",
        };

        // 3) Buscar si existe en la colección global de usuarios conductores
        //    (si no existe igual se crea invitación)
        let enrichedData = { ...baseData };

        try {
          const usersDriversRef = collection(
            db,
            `${process.env.REACT_APP_COL_USERS_DRIVERS}`
          );
          const q = query(
            usersDriversRef,
            where("email", "==", email),
            limit(1)
          );
          const snap = await getDocs(q);

          if (!snap.empty) {
            const u = snap.docs[0].data();
            enrichedData = {
              ...enrichedData,
              id: u?.id || "",
              bicciNumber: u?.bicciNumber || "",
            };
          }
        } catch (err) {
          // si falla, igual dejamos la invitación creada
          console.warn("No se pudo enriquecer con USERS_DRIVERS:", err);
        }

        // 4) Crear invitación en subcolección
        await setDoc(riderDocRef, enrichedData, { merge: true });

        // 5) Optimista en tabla (el hook igual refrescará)
        setRows((prev) => {
          const existsRow = prev.some((r) => normalizeEmail(r.email) === email);
          if (existsRow) return prev;

          return [
            ...prev,
            {
              id: enrichedData.id || `INVITED_${email}`, // si no hay id real
              name: "---",
              email,
              phone: "",
              bicciNumber: enrichedData.bicciNumber || "No Asignado",
              active: false,
              online: false,
              requireTracking: false,
              appVersion: "",
              vehicleID: "",
              hiddenListMenus: false,
              __invited: true,
            },
          ];
        });

        setShowEdit(false);
        setEmailRider("");
        setIsValid(null);
      } catch (err) {
        console.error(err);
        setError("No se pudo enviar la invitación.");
      } finally {
        setInviting(false);
      }
    },
    [companyId, emailRider, getCompanyDriversCollection]
  );

  const handleChangeEmail = (e) => {
    const value = e?.target?.value ?? "";
    setEmailRider(value);

    if (!value) {
      setIsValid(null);
      setError(null);
      return;
    }

    const ok = validateEmail(normalizeEmail(value));
    setIsValid(ok);
    setError(ok ? null : "Ingrese un email válido");
  };

  // ---------------------------------------------
  // Switches updates (drivers/{id})
  // ---------------------------------------------
  const updateDriverDoc = useCallback(async (driverId, patch) => {
    const driversCol = collection(db, "drivers");
    const driverRef = doc(driversCol, driverId);
    await updateDoc(driverRef, patch);
  }, []);

  const handleTrackingChange = useCallback(
    async (driverId, newValue) => {
      // Regla original: solo permitir activar (no desactivar)
      if (newValue !== true) return;

      try {
        await updateDriverDoc(driverId, { requireTracking: true });
        setRows((prev) =>
          prev.map((r) =>
            r.id === driverId ? { ...r, requireTracking: true } : r
          )
        );
      } catch (err) {
        console.error("Error actualizando tracking:", err);
      }
    },
    [updateDriverDoc]
  );

  const handleListMenusChange = useCallback(
    async (driverId, newValue) => {
      try {
        await updateDriverDoc(driverId, { hiddenListMenus: newValue });
        setRows((prev) =>
          prev.map((r) =>
            r.id === driverId ? { ...r, hiddenListMenus: newValue } : r
          )
        );
      } catch (err) {
        console.error("Error actualizando hiddenListMenus:", err);
      }
    },
    [updateDriverDoc]
  );

  // ---------------------------------------------
  // ✅ Delete from company subcollection
  // companies/{companyId}/companydrivers/{doc}
  // ---------------------------------------------
  const handleRemoveFromCompany = useCallback(
    async (row) => {
      if (!companyId) return;

      const email = normalizeEmail(row?.email);
      const driverId = row?.id || null;

      const confirmText = `¿Seguro que deseas desvincular este conductor de tu empresa?\n\nEmail: ${
        email || "-"
      }\nID: ${driverId || "-"}`;
      const ok = window.confirm(confirmText);
      if (!ok) return;

      const lockKey = driverId || email || "unknown";
      setDeletingKey(lockKey);

      try {
        const docRef = await findCompanyDriverDocRef({ driverId, email });

        if (!docRef) {
          alert(
            "No se encontró el documento del conductor en la subcolección de la empresa."
          );
          return;
        }

        await deleteDoc(docRef);

        // Optimista: quitar de la tabla
        setRows((prev) =>
          prev.filter((r) => {
            const sameId = driverId && r.id === driverId;
            const sameEmail = email && normalizeEmail(r.email) === email;
            return !(sameId || sameEmail);
          })
        );

        // También limpia selección si estaba seleccionado
        setSelectedIDs((prev) =>
          prev.filter((id) => id !== driverId && id !== `INVITED_${email}`)
        );
      } catch (err) {
        console.error("Error eliminando de subcolección:", err);
        alert("No se pudo desvincular el conductor. Revisa consola/logs.");
      } finally {
        setDeletingKey(null);
      }
    },
    [companyId, findCompanyDriverDocRef]
  );

  // ---------------------------------------------
  // Build rows from hook data
  // ---------------------------------------------
  useEffect(() => {
    if (!Array.isArray(ridersDataByCompany)) return;

    const filtered = ridersDataByCompany.filter(
      (r) => !developIDs.includes(r.id)
    );

    const mapped = filtered.map((r) => ({
      id: r.id,
      name: r.name || "",
      email: r.email || "",
      phone: r.phone || "",
      bicciNumber: r.bicciNumber || "",
      active: !!r.active,
      online: !!r.online,
      requireTracking: !!r.requireTracking,
      appVersion: r.appVersion || "",
      vehicleID: r.vehicleID || "",
      hiddenListMenus: !!r.hiddenListMenus,
    }));

    setRows(mapped);
  }, [ridersDataByCompany, developIDs]);

  // ---------------------------------------------
  // DataGrid columns (memo)
  // ---------------------------------------------
  const columns = useMemo(() => {
    const renderActions = (params) => {
      const driverId = params?.row?.id;
      const disabledDelete =
        deletingKey &&
        (deletingKey === driverId ||
          deletingKey === normalizeEmail(params?.row?.email));

      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Switch
              checked={params.row.hiddenListMenus || false}
              onChange={(e) =>
                handleListMenusChange(driverId, e.target.checked)
              }
              color="primary"
            />
            {params.row.hiddenListMenus ? (
              <VisibilityOffIcon />
            ) : (
              <VisibilityIcon />
            )}
          </Box>

          <IconButton
            sx={{ width: "30px !important", color: "#1e2158 !important" }}
            aria-label="Editar"
            onClick={() => viewDriverPerfil(driverId)}
          >
            <ModeEditIcon />
          </IconButton>

          <Tooltip title="Desvincular de mi empresa (borra en companydrivers)">
            <span>
              <IconButton
                sx={{ width: "30px !important" }}
                aria-label="Eliminar"
                disabled={!!disabledDelete}
                onClick={() => handleRemoveFromCompany(params.row)}
              >
                <DeleteForeverIcon sx={{ color: "red" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    };

    const renderTracking = (params) => {
      const alreadyOn = !!params.row.requireTracking;

      return (
        <Tooltip
          title={
            alreadyOn
              ? "Tracking activo (no se puede desactivar)"
              : "Activar tracking"
          }
        >
          <span>
            <Switch
              checked={alreadyOn}
              disabled={alreadyOn} // regla: solo activar
              onChange={(e) =>
                handleTrackingChange(params.row.id, e.target.checked)
              }
              color="primary"
            />
          </span>
        </Tooltip>
      );
    };

    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "name", headerName: "NOMBRE", width: 200 },
      { field: "email", headerName: "CORREO", width: 240 },
      { field: "phone", headerName: "TELÉFONO", width: 160 },
      { field: "bicciNumber", headerName: "BICCI NÚMERO", width: 80 },
      {
        field: "active",
        headerName: "ACTIVO",
        width: 90,
        renderCell: (p) => (p.value ? "Sí" : "No"),
      },
      {
        field: "online",
        headerName: "EN LÍNEA",
        width: 100,
        renderCell: (p) => (p.value ? "Sí" : "No"),
      },
      {
        field: "requireTracking",
        headerName: "TRACKING",
        width: 120,
        renderCell: renderTracking,
      },
      { field: "appVersion", headerName: "APP VER", width: 110 },
      { field: "vehicleID", headerName: "ID VEHÍCULO", width: 220 },
      {
        field: "actions",
        headerName: "ACCIONES",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: renderActions,
      },
    ];
  }, [
    deletingKey,
    handleListMenusChange,
    handleRemoveFromCompany,
    handleTrackingChange,
  ]);

  // ---------------------------------------------
  // Render
  // ---------------------------------------------
  return (
    <>
      <Grid2 container spacing={2}>
        <Grid2 sx={{ width: "100%", mb: 2 }} xs={12}>
          <h2 className="h2-withe">Mis Riders</h2>
        </Grid2>

        <Grid2 sx={{ width: "100%", mb: 2 }} xs={12}>
          <Card>
            <Box
              sx={{
                width: "100%",
                height: "auto",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Card sx={{ maxWidth: "350px" }}>
                {userdata?.userCompanyID ? (
                  <Box sx={{ p: 1 }}>
                    <Box sx={{ p: 1 }}> Invitar nuevos usuarios</Box>

                    <Tooltip
                      title={
                        "Es necesario completar los datos del conductor para su activación."
                      }
                    >
                      <Button
                        sx={{ m: 1 }}
                        color="primary"
                        variant="contained"
                        onClick={toggleShowEditNew}
                        disabled={inviting}
                      >
                        {showEdit ? (
                          <>
                            <HighlightOffIcon className="dashIcon-red" />
                            Cancelar
                          </>
                        ) : (
                          <>
                            <AddCircleIcon className="dashIcon-green" />
                            Agregar conductor
                          </>
                        )}
                      </Button>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    Debe registrar su empresa para agregar usuarios.
                  </Box>
                )}

                <Divider />

                <FadeInOut show={showEdit} duration={500}>
                  <ContainerViewComponent
                    className="formcontent"
                    id="micuentafromIni"
                  >
                    <hr />
                    <Form onSubmit={invitarUsuariosRider}>
                      <InputGroup className="mb-3">
                        <Form.Control
                          className={
                            isValid === false
                              ? "email-novalid"
                              : "email-isvalid"
                          }
                          placeholder="email"
                          type="email"
                          onChange={handleChangeEmail}
                          value={emailRider}
                          disabled={inviting}
                        />

                        <Button
                          type="submit"
                          variant="btn-primary"
                          id="button-addon2"
                          disabled={inviting || isValid !== true}
                        >
                          {inviting ? "enviando..." : "enviar"}
                        </Button>
                      </InputGroup>

                      {error ? (
                        <span className="alert alert-warning m-1">{error}</span>
                      ) : null}
                    </Form>
                  </ContainerViewComponent>
                </FadeInOut>

                <Divider />
              </Card>
            </Box>

            <CardHeader
              title="Conductores de mi empresa"
              subheader="Usuarios conductores vinculados con tu empresa"
            />

            <Divider />

            <ContainerViewComponent
              maxWidth={"100%"}
              className="formcontent"
              id="micuentafromIni"
            >
              <hr />

              <Box sx={{ width: "100%", minHeight: 420 }}>
                {!ridersDataByCompany ? (
                  <LinearProgress />
                ) : (
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={!Array.isArray(ridersDataByCompany)}
                    localeText={customLocaleText}
                    pageSizeOptions={[50, 100, 1000]}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 50 },
                      },
                      sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                    }}
                    slots={{ toolbar: GridToolbar }}
                    checkboxSelection
                    rowSelectionModel={selectedIDs}
                    onRowSelectionModelChange={(items) => setSelectedIDs(items)}
                    disableRowSelectionOnClick
                  />
                )}
              </Box>
            </ContainerViewComponent>

            <Divider />
          </Card>
        </Grid2>
      </Grid2>

      {showPopup ? (
        <Popup contenido={objRider} togglePopup={togglePopup} type={typePop} />
      ) : null}
    </>
  );
};

export default Drivers;
